import { Avatar } from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import AISparkle from "assets/icons/system/AISparkle"
import Bell from "assets/icons/system/Bell"
import BellRing from "assets/icons/system/BellRing"
import BookClosed from "assets/icons/system/BookClosed"
import BookOpenNew from "assets/icons/system/BookOpen"
import HomeLine from "assets/icons/system/HomeLine"
import HomeSmile from "assets/icons/system/HomeSmile"
import JobsSearch from "assets/icons/system/JobsSearch"
import ITOLogoGrey from "assets/logos/ito-mini-grey.svg"
import clsx from "clsx"
import { useExcludeDivider } from "components/Navigation/hooks/useExcludeDivider"
import Link, { NavBackground } from "components/Navigation/Link"
import PageContainer from "components/PageContainer"
import SuraasaPlusBadge, { PlusBadgeSize } from "components/SuraasaPlusBadge"
import { HOME_ONBOARDING_TARGETS } from "features/Home/HomeOnboarding/constants"
import { DASHBOARD_ONBOARDING_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/DashboardTour/constants"
import { useNotifications } from "features/Notifications/hooks/useNotifications"
import NotificationsPopup from "features/Notifications/NotificationsPopup"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { context } from "global/Context/context"
import { useFeatureToggle } from "global/FeatureToggleProvider"
import {
  Book,
  GraduationCap,
  Journal,
  MediaVideoList,
  MicrophoneSpeaking,
  Page,
  Shop,
  Suitcase,
} from "iconoir-react"
import { useContext, useEffect, useRef, useState } from "react"
import { Link as ReactRouterLink, useMatch } from "react-router-dom"
import routes from "routes"
import { breakpoints } from "utils/createBreakpoints"
import { buildUserName, getPlatformURL } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { ReactComponent as ItoLogo } from "../assets/OlympiadLogo.svg"
import ExploreMenu from "./ExploreMenu"
import ProfileMenuButton from "./ProfileMenuButton"
import Sidebar from "./Sidebar"
import WithoutAuth from "./WithoutAuth"

const moreLinks = [
  {
    key: "ito",
    name: "ITO 2022",
    link: routes.ito2022,
    icon: ItoLogo,
  },
  {
    key: "learnerHandbook",
    name: "Learner's Handbook",
    link: routes.learnerHandbook,
    icon: Book,
  },
  {
    key: "studentCatalog",
    name: "Student Catalog",
    link: routes.medStudentCatalog,
    icon: Book,
  },
  {
    name: "Store",
    link: getPlatformURL("suraasa", "/store"),
    icon: Shop,
  },
  {
    name: "Masterclasses",
    link: getPlatformURL("suraasa", "/masterclasses/all"),
    icon: MediaVideoList,
  },
  {
    name: "Blogs",
    link: getPlatformURL("suraasa", "/blog"),
    icon: Journal,
  },
  {
    name: "Podcasts",
    link: "https://podcasters.spotify.com/pod/show/suraasa",
    icon: MicrophoneSpeaking,
  },
]

type Props = {
  showSignInButton?: boolean
  showSuraasaPlusButton?: boolean
}

const TopNavigation = ({
  showSignInButton = true,
  showSuraasaPlusButton = true,
}: Props) => {
  const mdUp = useMediaQuery(breakpoints.up("md"))

  const [showNavDivider, setShowNavDivider] = useState(false)

  const handleScroll = () => {
    const scrollTop = window.scrollY

    const _getTopBarHeight = () => {
      const topBar = document.getElementById("top-heading-bar")
      if (topBar) {
        return topBar.clientHeight
      }
      return 0
    }

    const showNavDivider = scrollTop > _getTopBarHeight()

    setShowNavDivider(showNavDivider)
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleScroll()

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const { pageOverview } = useNotifications({
    useSearchParams: false,
    enableNotificationList: false,
  })
  const {
    cpat: { hasCpat },
  } = useEnrollments()
  const isAssignmentPage = useMatch(routes.assignment)

  const { data: notificationOverviewData } = pageOverview

  const featureToggle = useFeatureToggle()

  const isAIToolsEnabled = featureToggle.aiTools.isEnabled

  const [openSheet, setOpenSheet] = useState(false)

  const navContainerRef = useRef<HTMLDivElement>(null)

  const _omitDivider = useExcludeDivider()
  const omitDivider = showNavDivider ? false : _omitDivider

  const {
    pgctl: { isPGCTLUser },
    hasMed,
  } = useEnrollments()

  const { authInfo, itoParticipationDetails, profile } = useContext(context)

  const hasNotification =
    notificationOverviewData && notificationOverviewData.all > 0

  const userName = authInfo ? buildUserName(authInfo.user) : ""

  const toggleSheet = () => {
    setOpenSheet(!openSheet)
  }
  const { isActive } = useSuraasaPlusContext()

  if (!authInfo) {
    return <WithoutAuth showSignInButton={showSignInButton} />
  }

  if (!mdUp) {
    return (
      <nav
        id="topNavbar"
        className={clsx(
          "top-0 z-navbar flex items-center justify-between bg-white px-2 py-0.5 md:py-1",
          { sticky: !isAssignmentPage },
          { "border-b border-onSurface-200": !omitDivider }
        )}
        style={{
          transition: "top 0.3s",
        }}
      >
        <ReactRouterLink to={routes.home}>
          <img
            src="https://assets.suraasa.com/logos/v2/mini.webp"
            alt="suraasa logo"
            className="block size-3 md:hidden md:size-[35px]"
          />
          <img
            src="https://assets.suraasa.com/logos/v2/full-dark.webp"
            alt="suraasa logo"
            width={120}
            className="hidden md:block"
          />
        </ReactRouterLink>
        <div className="flex flex-row items-center gap-1.5">
          <button
            onClick={toggleSheet}
            className={clsx("relative", HOME_ONBOARDING_TARGETS.profile)}
          >
            <Avatar
              className={clsx("size-3 !border border-onSurface-200 md:size-4", {
                "border-surface shadow-[0px_3.93px_9.82px_0px_rgba(0,0,0,0.05)]":
                  isActive,
              })}
              src={profile.picture}
              name={userName}
            />
            {isActive && (
              <SuraasaPlusBadge
                size={PlusBadgeSize.xs}
                className="absolute -bottom-0.25 left-[3.5px] md:bottom-[-3px]"
              />
            )}
          </button>
        </div>

        <Sidebar
          open={openSheet}
          handleClose={toggleSheet}
          showSuraasaPlusButton={showSuraasaPlusButton}
        />
      </nav>
    )
  }

  const moreLinksToShow = moreLinks
    // Don't show Ai Tools route if disabled
    .filter(x => (isAIToolsEnabled ? true : x.key !== "aiTools")) // Don't show ITO links to non-participants
    .filter(x =>
      itoParticipationDetails.pastYears.isITO2022Participant
        ? true
        : x.key !== "ito"
    )
    .filter(x => (x.key === "learnerHandbook" ? isPGCTLUser : true))
    .filter(x => (x.key === "studentCatalog" ? hasMed : true))

  return (
    <nav
      id="topNavbar"
      className={clsx("top-0 z-navbar bg-white py-0.25", {
        sticky: !isAssignmentPage,
        "border-b border-onSurface-200": !omitDivider,
      })}
    >
      <PageContainer fullWidth className="px-2 sm:px-3 lg:px-4">
        <div className="flex items-center justify-between">
          <ReactRouterLink to={routes.home} className="shrink-0">
            <img
              src="https://assets.suraasa.com/logos/v2/mini.webp"
              alt="suraasa logo"
              height={35}
              width={35}
              className="block shrink-0 md:hidden"
            />
            <img
              src="https://assets.suraasa.com/logos/v2/full-dark.webp"
              alt="suraasa logo"
              width={95}
              className="hidden shrink-0 md:block"
            />
          </ReactRouterLink>
          <div className="flex w-full justify-center">
            {mdUp ? (
              <div
                className="relative flex items-center justify-between gap-2"
                ref={navContainerRef}
              >
                <NavBackground containerRef={navContainerRef} />
                <Link
                  href={routes.home}
                  text="Home"
                  icon={<HomeLine />}
                  activeIcon={<HomeSmile />}
                />
                <Link
                  href={routes.learning}
                  text="Learning"
                  icon={<BookClosed />}
                  activeIcon={<BookOpenNew className="mt-0.25" />}
                  className={clsx(DASHBOARD_ONBOARDING_TARGETS.learning)}
                />
                <Link
                  href={routes.masterclasses}
                  text="Masterclasses"
                  className={clsx(
                    DASHBOARD_ONBOARDING_TARGETS.masterclasses,
                    "mb-[2.5px]"
                  )}
                  icon={<GraduationCap height={22} width={22} />}
                  activeIcon={
                    <GraduationCap
                      height={22}
                      width={22}
                      className="mb-[2.5px]"
                    />
                  }
                />
                <Link
                  href={routes.aiTools.home}
                  className={clsx(
                    DASHBOARD_ONBOARDING_TARGETS.aiTools,
                    HOME_ONBOARDING_TARGETS.aiTools
                  )}
                  text="AI Tools"
                  icon={
                    <AISparkle
                      className="h-3"
                      height="20"
                      viewBox="0 0 20 20"
                    />
                  }
                />

                {hasCpat && (
                  <Link
                    href={routes.cpat.dashboard}
                    text="CPAT"
                    icon={<Page fontSize={13} className="mb-0.5" />}
                    activeIcon={<Page fontSize={13} className="mb-0.5" />}
                    className={clsx(
                      DASHBOARD_ONBOARDING_TARGETS.cpatAssessment
                    )}
                  />
                )}
                <Link
                  href={getPlatformURL("jobs", "/")}
                  text="Jobs"
                  icon={<Suitcase />}
                  activeIcon={<JobsSearch />}
                  className={clsx(
                    DASHBOARD_ONBOARDING_TARGETS.jobs,
                    HOME_ONBOARDING_TARGETS.jobs
                  )}
                />
                {itoParticipationDetails.registrationStatus === "completed" && (
                  <Link
                    href={routes.ito}
                    text="ITO"
                    icon={
                      <img
                        src={ITOLogoGrey}
                        height={22}
                        width={22}
                        alt="ito"
                        className="mb-[2.5px]"
                      />
                    }
                    activeIcon={
                      <img
                        src="https://assets.suraasa.com/ito/logos/ito-original-mini.svg"
                        height={22}
                        width={22}
                        alt="ito"
                        className="mb-[2.5px]"
                      />
                    }
                  />
                )}
              </div>
            ) : (
              <Link
                href="/home"
                text="Home"
                icon={<HomeLine />}
                activeIcon={<HomeSmile />}
              />
            )}
          </div>
          {mdUp && (
            <div className="flex items-center gap-1">
              <ExploreMenu moreLinks={moreLinksToShow} iconOnly />

              <div className={clsx("", { relative: hasNotification })}>
                <Link
                  isMenu
                  arrow={false}
                  isNotification
                  hasNotification={hasNotification}
                  text="Notifications"
                  icon={<Bell />}
                  activeIcon={<BellRing />}
                  menuChildren={<NotificationsPopup />}
                />
                {hasNotification && (
                  <span className="absolute left-2.5 top-0 z-2 inline-flex justify-center rounded-full bg-critical-500 px-0.5 text-xs font-semibold text-common-white-500">
                    {notificationOverviewData.all}
                  </span>
                )}
              </div>

              <ProfileMenuButton
                coverStyle={profile.coverStyle}
                src={profile.picture}
                name={userName}
              />
            </div>
          )}
        </div>
      </PageContainer>
    </nav>
  )
}
export default TopNavigation
